import type { Payload } from '@atlaskit/rovo-triggers/types';
import { encodeAdf } from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import type { FollowUpSource } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';
import { addSentryBreadcrumb } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { isConversationCancelled } from '@atlassian/conversation-assistant-store/controllers/conversation-cancellation';
import type { StartBackgroundAgentConversationCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/start-background-agent-conversation-command';

import type { useChatStore } from '../../chat-store/useChatStore';

type ChatActions = ReturnType<typeof useChatStore>[1];

/** The bridge acknowledges receipt immediately; this owns execution and fallback after that handoff. */
export async function startBackgroundConversation(
	command: StartBackgroundAgentConversationCommandProperties,
	{
		createSeededConversation,
		sendMessage,
	}: Pick<ChatActions, 'createSeededConversation' | 'sendMessage'>,
	storeUserMessages: boolean,
	publish: (payload: Payload) => void,
	onCreated?: (session: { sourceId: string; conversationId: string; agentId: string }) => void,
): Promise<void> {
	let conversationId: string | undefined;
	const openChat = () => {
		if (conversationId) {
			// Never send the prompt twice when creation succeeded but sending failed.
			publish({ type: 'chat-open', source: command.source, data: { channelId: conversationId } });
			return;
		}
		publish({
			type: 'chat-new',
			source: command.source,
			openChat: true,
			consumeOnce: true,
			data: {
				agentId: command.agentId,
				name: command.name,
				sourceId: command.sourceId,
				tags: command.tags,
				...(command.commentMentionContext
					? { commentMentionContext: command.commentMentionContext }
					: {}),
				prompt: command.prompt,
				dialogues: [],
				mode: { useCurrentPageContext: command.useCurrentPageContext },
			},
		});
	};
	try {
		conversationId = await createSeededConversation({
			name: command.name,
			sourceId: command.sourceId,
			tags: command.tags,
			dialogues: [],
			agentId: command.agentId,
			followUpSource: command.source as Exclude<FollowUpSource, 'none'>,
			selectConversation: false,
			mode: { useCurrentPageContext: command.useCurrentPageContext },
		});
		if (!conversationId) {
			addSentryBreadcrumb('Background comment invocation: seeded conversation unavailable');
			openChat();
			return;
		}
		onCreated?.({ sourceId: command.sourceId, conversationId, agentId: command.agentId });
		if (isConversationCancelled(conversationId)) {
			return;
		}
		// An explicit ID leaves any visible Chat conversation alone. Deliberately omit the
		// inline-input stream hook: CAP supplies comment progress and action handoff instead.
		await sendMessage({
			conversationId,
			prompt: encodeAdf(command.prompt),
			storeUserMessages,
			agentId: command.agentId,
			agentName: command.agentName,
			agentNamedId: command.agentNamedId,
			agentIdentityAccountId: command.agentIdentityAccountId,
			isThirdPartyAgent: command.isThirdPartyAgent,
			productKey: command.productKey,
			source: command.source,
			messageSources: {
				interactionSource: 'followUps',
				followUpSource: command.source as Exclude<FollowUpSource, 'none'>,
			},
			addCurrentPageAsContextUserPreference: command.useCurrentPageContext,
			...(command.commentMentionContext
				? { commentMentionContext: command.commentMentionContext }
				: {}),
		});
	} catch {
		// Chat still handles some send errors internally; only escaping errors reach this fallback.
		addSentryBreadcrumb('Background comment invocation failed; opening visible Chat');
		openChat();
	}
}
